import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Box,
} from "@mui/material";
import OfflinePinIcon from "@mui/icons-material/OfflinePin";
import { toast } from "react-toastify";

import useAxiosPrivate from "../../common/useAxiosPrivate";
import googleDriveAxios from "../../common/googleDriveAxios";
import { Checklist } from "../CustomComponents/ChecklistInterface";
import MultiSubheaderTableRowComponent from "../CustomComponents/SubheaderTableRowComponent";

interface CleaningQualityInspectionChecklistProps {
  chassisNo: string;
  setModel: React.Dispatch<React.SetStateAction<string>>;
  model: string;
  setCheckedBy: React.Dispatch<React.SetStateAction<string>>;
  isDisabled?: boolean;
}

const CleaningQualityInspectionChecklist: React.FC<
  CleaningQualityInspectionChecklistProps
> = ({ chassisNo, setModel, model, setCheckedBy }) => {
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState<boolean>(false);
  const [cleaningItems, setCleaningItems] = useState<Checklist[]>([]);
  const [checkedBy, setLocalCheckedBy] = useState<string>("");
  const [date, setDate] = useState<string>("");
  const [apiError, setApiError] = useState<boolean>(false);
  const [isRadioChanged, setIsRadioChanged] = useState<boolean>(false);
  const [completeStatus, setCompleteStatus] = useState<string>("Incomplete");
  const hasUnsavedChanges = chassisNo;

  const [activeCheckpointList, setActiveCheckpointList] = useState<any[]>([]);

  const checkCheckpointRecord = useCallback(async () => {
    if (!chassisNo) {
      return;
    }
    try {
      const queryParams = new URLSearchParams({
        chassis_no: "DC" + chassisNo,
        dept_name: "cleaning-quality-inspection",
      }).toString();

      const response = await googleDriveAxios.post(
        `/checkpoint-image-record?${queryParams}`
      );
      const data = response.data;
      setActiveCheckpointList(data.recorded_checkpoint_list);
    } catch (error) {
      console.error("Error checking images:", error);
    }
  }, [chassisNo]);

  useEffect(() => {
    checkCheckpointRecord();
  }, [checkCheckpointRecord]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const fetchData = async () => {
      if (!chassisNo || chassisNo.length < 4 || chassisNo.length > 5) {
        setCleaningItems([]);
        setApiError(false);
        setModel("");
        setCompleteStatus("Incomplete");
        setIsRadioChanged(false);
        return;
      }

      setLoading(true);
      setApiError(false);
      setModel("");
      try {
        const response = await axiosPrivate.post(
          `/prepare_cleaning_quality_inspection/${chassisNo}`
        );
        const data = response.data;

        const cleaningData = (data.cleaningItems || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status ? item.status.trim().toLowerCase() : "not checked",
          })
        );

        setCleaningItems(cleaningData);
        setLocalCheckedBy(data.checkedBy || "");
        setCheckedBy(data.checkedBy || "");
        setModel(data.model || "Default Model");

        let formattedDate = "";
        if (data.createdOn) {
          const parsedDate = new Date(data.createdOn);
          if (parsedDate.getFullYear() !== 1900) {
            formattedDate = data.createdOn ? data.createdOn.split("T")[0] : "";
          }
        }
        setDate(formattedDate);

        const allItems = [...cleaningData];
        const isComplete =
          allItems.length > 0 &&
          allItems.every(
            (item) =>
              item.status &&
              ["pass", "fail", "n/a"].includes(item.status.toLowerCase())
          );
        setCompleteStatus(isComplete ? "Complete" : "Incomplete");
        setIsRadioChanged(false);
      } catch (error: any) {
        if (error.response?.status === 500) {
          setApiError(true);
        } else {
          toast.error("Failed to fetch data.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [axiosPrivate, chassisNo, setCheckedBy, setModel]);

  const saveData = async () => {
    if (!date.trim()) {
      toast.error("Date is required.");
      return;
    }
    if (!checkedBy.trim()) {
      toast.error("Checked By field is required.");
      return;
    }

    setLoading(true);
    try {
      const allChecklists = [...cleaningItems];

      if (isRadioChanged) {
        const isComplete =
          allChecklists.length > 0 &&
          allChecklists.every(
            (item) =>
              item.status &&
              ["pass", "fail", "n/a"].includes(item.status.toLowerCase())
          );
        setCompleteStatus(isComplete ? "Complete" : "Incomplete");
      }

      const payload = {
        chassisNo,
        model,
        cleaningItems: cleaningItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        checkedBy,
        createdOn: date,
        completeStatus,
      };

      await toast.promise(
        axiosPrivate.post(
          "/create_or_update_cleaning_quality_inspection",
          payload
        ),
        {
          pending: "Saving data...",
          success: "Data saved successfully!",
          error: "Failed to save data.",
        }
      );

      setIsRadioChanged(false);

      if (checkedBy) {
        let itemNoCounter = 1;
        const newReworkItems = cleaningItems
          .filter((item) => item.status === "fail")
          .flatMap((item) => {
            const title = item.title || "No Title Available";
            const notesStr = item.notes || "";

            if (notesStr.includes(";")) {
              const parts = notesStr
                .split(";")
                .map((part) => part.trim())
                .filter((part) => part);
              return parts.map((note) => ({
                itemNo: itemNoCounter++,
                problemDescription: `${title}: ${note}`,
                comment: "",
                whoFoundFault: checkedBy,
                responsible: "",
                whoDidRework: "",
                timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                completed: false,
              }));
            }
            return [
              {
                itemNo: itemNoCounter++,
                problemDescription: `${title}: ${notesStr}`,
                comment: "",
                whoFoundFault: checkedBy,
                responsible: "",
                whoDidRework: "",
                timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                completed: false,
              },
            ];
          });

        if (newReworkItems.length > 0) {
          const reworkPayload = {
            chassisNo,
            model,
            station_name: "cleaning-quality-inspection",
            reworkItems: newReworkItems,
          };

          await axiosPrivate.post(`rework/save`, reworkPayload, {
            params: { rework_type: "cleaning-quality-inspection" },
          });
        }
      }
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Failed to save data.");
    } finally {
      setLoading(false);
    }
  };

  const handleNotesChange = (index: number, value: string, _type: string) => {
    if (completeStatus === "Complete") return;

    setCleaningItems((prev) => {
      const updated = [...prev];
      updated[index].notes = value;
      return updated;
    });
  };

  const handleRadioChange = (index: number, value: string, _type: string) => {
    if (completeStatus === "Complete") return;

    setIsRadioChanged(true);

    setCleaningItems((prev) => {
      const updated = [...prev];
      updated[index].status = value.toLowerCase();
      return updated;
    });
  };

  const allChecklists = [...cleaningItems];

  const allNotChecked =
    allChecklists.length > 0 &&
    allChecklists.every((item) => item.status?.toLowerCase() === "not checked");

  const checklistsWithSerialNumbers = [
    {
      data: cleaningItems,
      type: "cleaning",
      subHeaderText: "Cleaning",
    },
  ];

  let serialNumberCounter = 0;

  let tableBodyContent: React.ReactNode;

  if (loading) {
    tableBodyContent = (
      <TableRow>
        <TableCell colSpan={5} align="center">
          <Box
            sx={{
              position: "absolute",
              top: "60%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10,
            }}
          >
            <CircularProgress />
          </Box>
        </TableCell>
      </TableRow>
    );
  } else if (apiError) {
    tableBodyContent = (
      <TableRow>
        <TableCell colSpan={5} align="center">
          <Typography color="error">Invalid DC Number</Typography>
        </TableCell>
      </TableRow>
    );
  } else {
    tableBodyContent = (
      <React.Fragment>
        {checklistsWithSerialNumbers.map((checklistSection) => {
          const serialNumberStart = serialNumberCounter + 1;
          serialNumberCounter += checklistSection.data.length;

          return (
            <MultiSubheaderTableRowComponent
              key={checklistSection.type}
              subHeaderText={checklistSection.subHeaderText}
              type={checklistSection.type}
              data={checklistSection.data}
              handleNotesChange={handleNotesChange}
              handleRadioChange={handleRadioChange}
              disabled={completeStatus === "Complete"}
              allNotChecked={allNotChecked}
              serialNumberStart={serialNumberStart}
              chassisDCNumber={"DC" + chassisNo}
              deptName={"cleaning-quality-inspection"}
              activeCheckpointList={activeCheckpointList}
              checkCheckpointRecord={checkCheckpointRecord}
            />
          );
        })}
      </React.Fragment>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        filter: loading ? "blur(5px)" : "none",
        pointerEvents: loading ? "none" : "auto",
      }}
    >
      <Typography
        sx={{
          mb: 2,
          fontWeight: "600",
          display: "flex",
          alignItems: "center",
          gap: "5px",
          color: completeStatus === "Incomplete" ? "error.main" : "success.main",
        }}
      >
        {completeStatus === "Incomplete" ? (
          <>⚠️ INCOMPLETE</>
        ) : (
          <>
            <OfflinePinIcon /> COMPLETE
          </>
        )}
      </Typography>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow className="table__head_row">
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              ></TableCell>
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              >
                S/N
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                CHECKLIST
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                NOTES
              </TableCell>
              <TableCell
                className="table__head_cell"
                sx={{ width: "5%" }}
                align="center"
              >
                STATUS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{tableBodyContent}</TableBody>
        </Table>
      </TableContainer>

      <Grid container spacing={3} my={4}>
        <Grid item md={4}>
          <TextField
            label="Checked By"
            value={checkedBy}
            onChange={(e) => {
              if (completeStatus === "Complete") return;
              setLocalCheckedBy(e.target.value);
              setCheckedBy(e.target.value);
            }}
            fullWidth
            hidden={apiError}
            disabled={completeStatus === "Complete"}
          />
        </Grid>
        <Grid item md={4}>
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => {
              if (completeStatus === "Complete") return;
              setDate(e.target.value);
            }}
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
            hidden={apiError}
            disabled={completeStatus === "Complete"}
          />
        </Grid>
      </Grid>

      <Button
        variant="contained"
        color="primary"
        onClick={saveData}
        disabled={
          allChecklists.length === 0 ||
          apiError ||
          loading ||
          completeStatus === "Complete"
        }
      >
        Save
      </Button>
    </Box>
  );
};

export default CleaningQualityInspectionChecklist;
